import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftRight, Download, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { LoadingSpinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { MAP_COLORS } from "@/lib/map-colors";
import { PointField, type PointFieldHandlers } from "@/components/point-field";
import type { SuggestionFeature } from "@/hooks/use-suggested-routes";

interface SuggestedRoutesPanelProps {
  visible: boolean;
  distancePresets: readonly number[];
  distanceKm: number;
  onDistanceChange: (km: number) => void;
  loop: boolean;
  onLoopChange: (loop: boolean) => void;
  hasEnd: boolean;
  // Bias place-search results toward the city center.
  searchBias?: [number, number];
  startField: PointFieldHandlers;
  endField: PointFieldHandlers;
  candidates: SuggestionFeature[];
  selectedId: number | null;
  onSelect: (feature: SuggestionFeature) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  // A route is picked and drawn, so it can be reversed or exported.
  hasRoute: boolean;
  onReverse: () => void;
  onExportGPX: () => void;
  // Total city network length (km) for the per-route coverage percent, or
  // undefined while the workspace progress is still loading.
  totalNetworkKm?: number;
}

export const SuggestedRoutesPanel = ({
  visible,
  distancePresets,
  distanceKm,
  onDistanceChange,
  loop,
  onLoopChange,
  hasEnd,
  searchBias,
  startField,
  endField,
  candidates,
  selectedId,
  onSelect,
  onGenerate,
  isGenerating,
  totalNetworkKm,
  hasRoute,
  onReverse,
  onExportGPX,
}: SuggestedRoutesPanelProps) => {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 12 }}
          transition={{ duration: 0.15 }}
          className="absolute right-4 top-16 z-10 w-64 rounded-md bg-white p-3 shadow"
        >
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-stone-700">
            <Sparkles
              className="h-4 w-4"
              style={{ color: MAP_COLORS.suggestedRoute }}
            />
            Suggested routes
          </div>
          <p className="mb-3 text-xs text-stone-500">
            Cover streets you haven&apos;t run yet from a start point.
          </p>

          {/* Start point: search a place, choose on map, or use current location. */}
          <div className="mb-2">
            <PointField role="start" bias={searchBias} {...startField} />
          </div>

          {/* End point (optional). When set, routes go start to end. */}
          <div className="mb-3">
            <PointField role="end" bias={searchBias} {...endField} />
          </div>

          {/* Distance */}
          <div className="mb-3">
            <div className="mb-1 text-xs font-medium text-stone-600">
              Distance
            </div>
            <div className="flex gap-1">
              {distancePresets.map((km) => (
                <Button
                  key={km}
                  variant={km === distanceKm ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => onDistanceChange(km)}
                >
                  {km} km
                </Button>
              ))}
            </div>
          </div>

          {/* Loop toggle - not applicable once an end point is set. */}
          <label
            className={cn(
              "mb-3 flex items-center justify-between",
              hasEnd ? "cursor-not-allowed opacity-50" : "cursor-pointer",
            )}
          >
            <span className="text-xs font-medium text-stone-600">
              {hasEnd ? "Loop (end point set)" : "Loop (start = finish)"}
            </span>
            <Switch
              checked={loop && !hasEnd}
              disabled={hasEnd}
              onCheckedChange={onLoopChange}
            />
          </label>

          <Button
            className="w-full gap-2"
            onClick={onGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <LoadingSpinner size="sm" />
                Generating...
              </>
            ) : (
              "Generate routes"
            )}
          </Button>

          {/* Candidate list (only the rows scroll, so the controls stay put) */}
          {candidates.length > 0 && (
            <div className="mt-3 flex max-h-[40vh] flex-col gap-1 overflow-y-auto border-t border-stone-200 pt-3">
              {candidates.map((c, i) => {
                const coveragePct =
                  totalNetworkKm && totalNetworkKm > 0
                    ? (c.properties.new_km / totalNetworkKm) * 100
                    : null;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => onSelect(c)}
                    className={cn(
                      "flex items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors",
                      c.id === selectedId
                        ? "bg-stone-100 font-medium"
                        : "hover:bg-stone-50",
                    )}
                  >
                    <span
                      className="h-2.5 w-2.5 flex-none rounded-full"
                      style={{ backgroundColor: MAP_COLORS.suggestedRoute }}
                    />
                    <span className="text-stone-700">Route {i + 1}</span>
                    <span className="ml-auto tabular-nums text-stone-500">
                      {(c.properties.distance_m / 1000).toFixed(1)} km ·{" "}
                      {c.properties.new_streets} st
                      {coveragePct !== null && (
                        <span className="ml-1 font-medium text-emerald-600">
                          +{coveragePct.toFixed(1)}%
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Actions for the picked route, shown once it has drawn. Gated on a
              live selection too, so a Generate (which clears selectedId but not
              the drawn geometry) hides them until a new route is picked. */}
          {selectedId !== null && hasRoute && (
            <div className="mt-3 flex gap-2 border-t border-stone-200 pt-3">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-2"
                onClick={onReverse}
              >
                <ArrowLeftRight className="h-4 w-4" />
                Reverse
              </Button>
              <Button size="sm" className="flex-1 gap-2" onClick={onExportGPX}>
                <Download className="h-4 w-4" />
                GPX
              </Button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
